/**
 * BOARD-AS-SPEC-1 (s222, D-221.40) — the HAND-KEPT half: what the board cannot say.
 *
 * A board's cell keys (`k`) are its own names, not product field keys, and its picker wording is what the
 * planner reads, not what is stored. This file is the one place that links the two. It is checked by
 * `npm run check:board-parity`, which FAILS on:
 *   • a key anywhere in the board's screen data that is not classified in `BUILD_KEYS` below;
 *   • a board cell `k` that is not in `CELL_FIELD` for its screen (or a `CELL_FIELD` entry the board lacks);
 *   • on a BUILT screen, a picker / segment option with no stored value in `OPTION_VALUE` (or a stale one).
 * 🔴 An unmapped key is never silently dropped — that is exactly the drift this lane exists to end.
 *
 * Storage, gating and write paths stay in the screens' own code; nothing here decides where a value goes.
 */

import { FIELD_KEYS } from "../constants/fields";

/** Every structural key the board's `steps()` output may carry, classified (Phase 0, s222). */
export const BUILD_KEYS = {
  /** The screen reads it straight from the data. */
  straight: [
    "key", "crumb", "title", "why",
    "groups", "cols", "tiles", "id", "label", "sub", "mark", "color", "none", "delta",
    "headers", "strong", "cells", "kind", "ph", "opts", "word", "undo", "org", "long",
    "bigTitle", "bigHint", "bigPh", "rowsLead", "rowsNote", "second", "noteFor", "pickNotes", "noDup",
    // s232 (CHAPTER-TWO-ENGINE-1): chapter two's own shape — a `choice` of `options` (tiles, grouped by `grp`, `multi`
    // on Other property), each opening `lines` (stacked questions) or `rows` (a table starting at `start` rows), with a
    // `lead` above; line sizing (`w`, `big`, `inline`), hint and placeholder words, a typed Other's words, the `foot`.
    "options", "grp", "multi", "lines", "rows", "start", "lead", "hint", "text", "w", "big", "inline", "otherLabel", "otherPh", "foot",
    // s232 (PR B, rule 137): the will cell's two card titles, declared on the board (the person trigger reuses `word`).
    "cardTitles",
    // s232 (D-232.91, board v417): the backup standing's not-told note — words the screen shows.
    "untoldNote",
    // s233 (D-232.16): a backup row's own told choice — ExecScreen draws it from the data (`ph`, `opts`, `untoldNote`).
    "backupStanding",
    // s233 (D-233.14, D-233.37): a named executor's standing dropdown — ExecScreen draws it from the data (`ph`, `opts`).
    "execStanding",
    // s233 (D-233.16 point 1, D-233.35(7)): Your home's blocks pair their short answers — Ch2Lines draws it from the data.
    "pairs",
    // s233 (D-233.81, board v440 merged): the combined Home and property screen — a tile group's heading (`head`), one of
    // three (`one`), the tile that pins the home as row 1 of a table (`pinTo`), the pinned row's own lines and note
    // (`pinLines`, `pinNote`), a block on one line (`oneLine`), the step's 52px tiles (`compact`), a table's Add-row words
    // (`addLabel`). Ch2Screens reads each from the data.
    "head", "one", "pinTo", "pinLines", "pinNote", "oneLine", "compact", "addLabel",
    // s236 (D-236.29(a), rule 150): `orgBy` — a joint cell's company card by TILE (the charity tile on benef), as `titleBy`.
    "orgBy",
    // s237 chapter three PR 2 (D-236.61): the shared tile-to-row engine reads `pairNone` (one tile and its none side by side)
    // and `noLead` (a table with no lead column — Past employers, D-237.25) from the data.
    "pairNone", "noLead",
    // s236 (D-236.87): a date's `emptyThen` — follow-up questions on the row's second line while the date is empty (Children's
    // Under 18?). The screen draws it from the data through the chapter-one row tables' second line.
    "emptyThen",
  ],
  /** Needs this map (`k`) or a mechanism the product has not built yet (Lane 2 onward). */
  // s229 (D-229.28): `then` — a follow-up line drawn under one answer (the faith line on `daily`). No screen
  // renders it yet; owed to CARE-SHEET-1's build (findings item 32). Its children are keyed by answer word.
  // s231 (D-231.59): `rowOnly`, `numbered`, `firstOnly`, `addsRow` — the executor screen's backup column and its Backup
  // row; built s232 (BACKUP-NUDGE-1 PR 1): ExecScreen reads `rowOnly` and implements the other three as the board declares.
  mapOrNew: ["k", "typed", "fromPlan", "fromKid", "onWhen", "vIn", "rules", "autoPick", "nextNote", "noStanding", "then",
    "rowOnly", "numbered", "firstOnly", "addsRow",
    // s232: `offWhen` / `unless` (`k`, `v`) — a line switched off by another answer in the same block, and back on (the
    // s225 build requirement on the owned-place block's "Left to someone?"). Built with chapter two's screens (PR B).
    "offWhen", "unless", "v",
    // s232 (D-232.91, board v417): `hidesRowOn` — No / Not sure hides the Backup rows (built: ExecScreen, D-232.48).
    // s233 (D-232.16): `backupStanding` is BUILT — moved to `straight` (ExecScreen reads its words from the data).
    "hidesRowOn"],
  /** The canvas's own bookkeeping. `prune` is also read by the shell's counter until INTERVIEW-COUNTER-TRUTH-1. */
  // s232: `cover` is chapter two's "ready" bump on a tile (read by the counter when chapter two is built, like `prune`);
  // `footLink` and its `note` are PREVIEW-ONLY on the board ("DELETE AT BUILD" — in the product the link opens Your Details).
  canvasOnly: ["prune", "full", "sample", "canvasLink", "cover", "footLink", "note"],
} as const;

/**
 * s237 (chapter three PR 2; rule 159): a money box is known by the board cell's KEY, never its words (s233 D-233.81 — v440
 * renamed "Roughly worth" to "Estimated value" and a label test would have lost the dollars). ONE declaration for every
 * engine: chapter two's (worth, owing, monthly, depamt) and chapter three's (worth, amount, cost).
 */
export const MONEY_CELL_KEYS: ReadonlySet<string> = new Set(["worth", "owing", "monthly", "depamt", "amount", "cost"]);
export const isMoneyCell = (k: string | undefined): boolean => MONEY_CELL_KEYS.has(k ?? "");

/** Keys whose CHILDREN are data (option words, tile ids), not structure — the census does not descend into them. */
export const DATA_MAP_KEYS = ["pickNotes", "noteFor", "rules", "then", "orgBy"] as const;

/** Screens the product builds from the data today. Every other screen carries data only (Lane 2 builds them). */
// s224 (RULE 137): `pets` joins the list — its cells are mapped below, so `check:board-parity`
// enforces the board against it from now on. That enforcement is the point of the lane: `pets`
// is the screen that drifted, and it drifted because nothing compared it to anything.
// s232 (chapter two, PR B): Your home, Other property, Vehicles and Valuables join — read and written from the maps below.
// s232 PR C: Firearms and Safes join.
// s237 chapter three PR 2: Work joins — the first screen of the shared tile-to-row engine (D-236.61).
export const BUILT_SCREENS = ["kids", "daily", "exec", "benef", "ids", "pets", "prop", "veh", "val", "fa", "safes", "work"] as const;

/**
 * Board cell key → the product field key(s) its answer lands in, per screen. `null` = the screen is not built
 * yet; the key is ACCOUNTED FOR (so a new one on the board still fails) but maps nowhere until Lane 2.
 */
export const CELL_FIELD: Readonly<Record<string, Readonly<Record<string, readonly string[] | null>>>> = {
  kids: {
    who: [FIELD_KEYS.FIRST_NAME, FIELD_KEYS.SURNAME],
    dob: [FIELD_KEYS.DOB],
    crel: [FIELD_KEYS.RELATIONSHIP],
    guard: [FIELD_KEYS.PRIMARY_GUARDIAN, FIELD_KEYS.BACKUP_GUARDIAN],
    inwill: [FIELD_KEYS.GUARDIAN_NAMED_IN_WILL],
    dwho: [FIELD_KEYS.FIRST_NAME, FIELD_KEYS.SURNAME],
    ddob: [FIELD_KEYS.DOB],
    drel: [FIELD_KEYS.RELATIONSHIP],
    dguard: [FIELD_KEYS.PRIMARY_GUARDIAN, FIELD_KEYS.BACKUP_GUARDIAN],
  },
  daily: {
    doc: [FIELD_KEYS.DOCTOR_NAME],
    health: [FIELD_KEYS.CONDITIONS],
    school: [FIELD_KEYS.SCHOOL_NAME],
    grade: [FIELD_KEYS.GRADE],
    day: [FIELD_KEYS.ROUTINES],
    faith: [FIELD_KEYS.RAISED_IN_FAITH],
  },
  spouse: { who: null, dob: null, since: null, kidsfrom: null, support: null },
  // s225 (Reade: "1 approved") — Military or RCMP service is DRAWN, not built: like `spouse`, its cells are accounted for
  // and map nowhere until Lane 2 builds the screen and its storage (s225-schema-followup-canvas.md §1).
  mil: { now: null, pension: null },
  // s224 (D-223.36) — the FIRST table is the pet row, the SECOND is the vet row, and both are rows of
  // `family-pets` told apart by `row_kind` (the `kids` precedent, D-221.10). `who` and `call` are the
  // ONE standard contact card: the name field is listed here, and the card also writes that person's
  // phone, email and address onto the row (the `exec` / `benef` convention).
  // s237 chapter three PR 2 — Work (Chapter3.dc.html `work`): job / other-work rows are Career rows, own-business /
  // self-employed rows are Business rows (D-237.27). The employer is ONE company card (D-235.72): its name (payer_name, the
  // linked company), its phone / email, the address the letter goes to, and the person to ask for — the HR contact (W6).
  work: {
    call: [FIELD_KEYS.EMPLOYER, FIELD_KEYS.EMPLOYER_PHONE, FIELD_KEYS.EMPLOYER_EMAIL, FIELD_KEYS.EMPLOYER_ADDRESS,
      FIELD_KEYS.HR_CONTACT_NAME, FIELD_KEYS.HR_CONTACT_PHONE, FIELD_KEYS.HR_CONTACT_EMAIL, FIELD_KEYS.HR_CONTACT_ADDRESS],
    empno: [FIELD_KEYS.EMPLOYEE_NUMBER],
    kind: [FIELD_KEYS.BIZ_TYPE],
    role: [FIELD_KEYS.ROLE],
    worth: [FIELD_KEYS.VALUE],
    takes: [FIELD_KEYS.SUCCESSION_CONTACT, FIELD_KEYS.SUCCESSION_CONTACT_PHONE, FIELD_KEYS.SUCCESSION_CONTACT_EMAIL, FIELD_KEYS.SUCCESSION_CONTACT_ADDRESS],
    // D-235.73: up to three co-owners on the card, each a business_co_owners row.
    co: [FIELD_KEYS.JOINT_OWNER_NAME, FIELD_KEYS.JOINT_OWNER_PHONE, FIELD_KEYS.JOINT_OWNER_EMAIL, FIELD_KEYS.JOINT_OWNER_ADDRESS,
      FIELD_KEYS.CO_OWNER_2, FIELD_KEYS.CO_OWNER_2_PHONE, FIELD_KEYS.CO_OWNER_2_EMAIL, FIELD_KEYS.CO_OWNER_2_ADDRESS,
      FIELD_KEYS.CO_OWNER_3, FIELD_KEYS.CO_OWNER_3_PHONE, FIELD_KEYS.CO_OWNER_3_EMAIL, FIELD_KEYS.CO_OWNER_3_ADDRESS],
  },
  // Coverage and Past employers are drawn, not built (chapter three PR 3): accounted for, mapped nowhere yet.
  cover: { inst: null, amount: null, cost: null },
  old: { call: null, why: null, left: null },
  pets: {
    kind: [FIELD_KEYS.PET_TYPE],
    name: [FIELD_KEYS.PET_NAME],
    who: [FIELD_KEYS.GUARDIAN],
    // s236 (D-236.25(3)): the vet is ONE company card — `practice` is gone from the board; the company's name also writes
    // the role's label (vet_practice, D-236.40(1)).
    call: [FIELD_KEYS.VET_CONTACT],
  },
  // s223 (D-222.10): legal-your-executor. The name is a TEXT field (the card writes its words); the card also
  // writes the row's phone, email and address.
  // s232 (D-232.8): `bk` — "Does your will name a backup executor?" — is BUILT, and its answer is the PLAN's, not a
  // row's: it lands on `clients.will_names_backup` (CELL_CLIENT_COLUMN below). No field holds it, so it stays `null` here.
  exec: { who: [FIELD_KEYS.NAME], rel: [FIELD_KEYS.RELATIONSHIP], bk: null },
  // s223 (D-222.10 part 4): beneficiaries. `who` is the row's own name parts (a charity's whole name in the surname,
  // "or organization name"); the card also writes the row's phone, email and address.
  benef: {
    who: [FIELD_KEYS.BENEFICIARY_FIRST_NAME, FIELD_KEYS.BENEFICIARY_SURNAME],
    rel: [FIELD_KEYS.BENEFICIARY_RELATIONSHIP],
    minor: [FIELD_KEYS.BENEFICIARY_MINOR],
    dob: [FIELD_KEYS.BENEFICIARY_DOB],
  },
  // s223 (D-222.10 part 5): the row's identity is its `id_type` (the tile); the screen asks only where it is kept.
  ids: { where: [FIELD_KEYS.LOCATION] },

  // ── Chapter two (Chapter2.dc.html), s232 (CHAPTER-TWO-ENGINE-1, PR A) ──────────────────────────────────────────
  // Every cell is ACCOUNTED FOR and maps nowhere yet — no chapter-two screen is built (the `spouse` / `mil` precedent).
  // A cell is named `<tile id>.<k>` (scripts/lib/board-parity.ts `optionCells`): the same question under two tiles is
  // two answers with two homes. PR B gives each its typed column (s232 rulings 1–6); the insurer lines stay drawn until
  // the Insurance part merges (ruling 5).
  // Built (PR B). A person cell lists its fields in the card's order: the name, then its phone, email and address
  // (s235 PR A, rule 63 — every chapter-two card carries all three). Owned places (the home's "I own it" and Other property's "I own other places") are Properties
  // rows; "I rent" / "I live with family" and rented places are Things I Rent rows. The house-insurance block is DRAWN
  // (ruling 5: it waits for the Insurance part) — see DRAWN_CELLS.
  // s233 (D-233.81): Your home is part of this screen — "Where you live" pins the home as row 1 of the own or rent
  // table (the same cells as every row), and the family block's four cells are here. s234 (CH2-STORAGE-PR3): Rented out?
  // and its tenant card are built — rented_out, and the tenant by reference (tenant_person_id).
  prop: {
    "own.kind": [FIELD_KEYS.PROP_TYPE],
    "own.rentout": [FIELD_KEYS.RENTED_OUT],
    "own.tenant": [FIELD_KEYS.TENANT_NAME, FIELD_KEYS.TENANT_PHONE, FIELD_KEYS.TENANT_EMAIL, FIELD_KEYS.TENANT_ADDRESS],
    "own.lmrtaken": [FIELD_KEYS.LAST_MONTH_DEPOSIT_TAKEN],
    "own.depamt": [FIELD_KEYS.SECURITY_DEPOSIT], // s235 (D-235.19): Yes asks how much — "Security deposit held"
    "own.addr": [FIELD_KEYS.ADDRESS],
    "own.worth": [FIELD_KEYS.VALUE],
    "own.coowner": [FIELD_KEYS.JOINT_OWNER_NAME, FIELD_KEYS.JOINT_OWNER_PHONE, FIELD_KEYS.JOINT_OWNER_EMAIL, FIELD_KEYS.JOINT_OWNER_ADDRESS],
    "own.tenure": [FIELD_KEYS.TITLE_OWNERSHIP],
    "own.gift": [FIELD_KEYS.JOINT_GIFT_INTENT],
    "own.will": [FIELD_KEYS.LEFT_TO, FIELD_KEYS.LEFT_TO_PERSON, FIELD_KEYS.LEFT_TO_PERSON_PHONE, FIELD_KEYS.LEFT_TO_PERSON_EMAIL, FIELD_KEYS.LEFT_TO_PERSON_ADDRESS], // s234 (PR 3): Yes / No / Not sure; s235: + the card
    "own.mortgage": [FIELD_KEYS.HAS_MORTGAGE],
    "own.lender": [FIELD_KEYS.MORTGAGE_LENDER, FIELD_KEYS.MORTGAGE_LENDER_PHONE, FIELD_KEYS.MORTGAGE_LENDER_EMAIL, FIELD_KEYS.MORTGAGE_LENDER_ADDRESS, FIELD_KEYS.MORTGAGE_LENDER_ASK_FOR, FIELD_KEYS.MORTGAGE_LENDER_ASK_FOR_PHONE, FIELD_KEYS.MORTGAGE_LENDER_ASK_FOR_EMAIL, FIELD_KEYS.MORTGAGE_LENDER_ASK_FOR_ADDRESS], // s235 PR B (D-235.18(3)): the company card
    "own.owing": [FIELD_KEYS.MORTGAGE_BALANCE],
    "own.monthly": [FIELD_KEYS.MORTGAGE_PAYMENT],
    "own.cosign": [FIELD_KEYS.MORTGAGE_CO_BORROWER, FIELD_KEYS.MORTGAGE_CO_BORROWER_PHONE, FIELD_KEYS.MORTGAGE_CO_BORROWER_EMAIL, FIELD_KEYS.MORTGAGE_CO_BORROWER_ADDRESS],
    "own.protect": [FIELD_KEYS.MORTGAGE_INSURED],
    // s235 PR B (D-235.20(4), rule 150): the insurer and the broker are company cards on the asset's ONE policy.
    "own.ins": [FIELD_KEYS.HAS_INSURANCE],
    "own.insurer": [FIELD_KEYS.POLICY_INSURER, FIELD_KEYS.POLICY_INSURER_PHONE, FIELD_KEYS.POLICY_INSURER_EMAIL, FIELD_KEYS.POLICY_INSURER_ADDRESS, FIELD_KEYS.POLICY_INSURER_ASK_FOR, FIELD_KEYS.POLICY_INSURER_ASK_FOR_PHONE, FIELD_KEYS.POLICY_INSURER_ASK_FOR_EMAIL, FIELD_KEYS.POLICY_INSURER_ASK_FOR_ADDRESS],
    "own.brk": [FIELD_KEYS.POLICY_DEALT_WITH],
    "own.broker": [FIELD_KEYS.POLICY_BROKER, FIELD_KEYS.POLICY_BROKER_PHONE, FIELD_KEYS.POLICY_BROKER_EMAIL, FIELD_KEYS.POLICY_BROKER_ADDRESS, FIELD_KEYS.POLICY_BROKER_ASK_FOR, FIELD_KEYS.POLICY_BROKER_ASK_FOR_PHONE, FIELD_KEYS.POLICY_BROKER_ASK_FOR_EMAIL, FIELD_KEYS.POLICY_BROKER_ASK_FOR_ADDRESS],
    "own.keys": [FIELD_KEYS.KEY_LOCATION],
    "rent.kind": [FIELD_KEYS.HOUSING_TYPE],
    "rent.addr": [FIELD_KEYS.RENTAL_ADDRESS],
    "rent.landlord": [FIELD_KEYS.LANDLORD, FIELD_KEYS.LANDLORD_PHONE, FIELD_KEYS.LANDLORD_EMAIL, FIELD_KEYS.LANDLORD_ADDRESS, FIELD_KEYS.LANDLORD_ASK_FOR, FIELD_KEYS.LANDLORD_ASK_FOR_PHONE, FIELD_KEYS.LANDLORD_ASK_FOR_EMAIL, FIELD_KEYS.LANDLORD_ASK_FOR_ADDRESS], // s235 PR B: + the person to ask for
    "rent.monthly": [FIELD_KEYS.MONTHLY_RENT],
    "rent.lease": [FIELD_KEYS.LEASE_TERM],
    "rent.leaseend": [FIELD_KEYS.LEASE_END],
    "rent.lmrpaid": [FIELD_KEYS.LAST_MONTH_DEPOSIT_PAID],
    "rent.depamt": [FIELD_KEYS.SECURITY_DEPOSIT], // s235 (D-235.18(2)): Yes asks how much — Things I Rent's "Security Deposit"
    "rent.keys": [FIELD_KEYS.KEY_LOCATION],
    "fam.whose": [FIELD_KEYS.LANDLORD, FIELD_KEYS.LANDLORD_PHONE, FIELD_KEYS.LANDLORD_EMAIL, FIELD_KEYS.LANDLORD_ADDRESS, FIELD_KEYS.LANDLORD_ASK_FOR, FIELD_KEYS.LANDLORD_ASK_FOR_PHONE, FIELD_KEYS.LANDLORD_ASK_FOR_EMAIL, FIELD_KEYS.LANDLORD_ASK_FOR_ADDRESS], // s235 PR B: + the person to ask for
    "fam.care": [FIELD_KEYS.HOUSING_TYPE],
    "fam.residence": [FIELD_KEYS.RESIDENCE_NAME, FIELD_KEYS.RESIDENCE_PHONE, FIELD_KEYS.RESIDENCE_EMAIL, FIELD_KEYS.RESIDENCE_ADDRESS, FIELD_KEYS.RESIDENCE_ASK_FOR, FIELD_KEYS.RESIDENCE_ASK_FOR_PHONE, FIELD_KEYS.RESIDENCE_ASK_FOR_EMAIL, FIELD_KEYS.RESIDENCE_ASK_FOR_ADDRESS], // s235 PR B: + the person to ask for
    "fam.deposit": [FIELD_KEYS.PREPAID_DEPOSIT],
  },
  veh: {
    "yes.kind": [FIELD_KEYS.VEHICLE_TYPE],
    "yes.year": [FIELD_KEYS.YEAR],
    "yes.make": [FIELD_KEYS.MAKE_MODEL],
    "yes.where": [FIELD_KEYS.VEHICLE_LOCATION],
    "yes.worth": [FIELD_KEYS.VALUE],
    "yes.loan": [FIELD_KEYS.HAS_LOAN],
    "yes.lender": [FIELD_KEYS.LOAN_LENDER, FIELD_KEYS.LOAN_LENDER_PHONE, FIELD_KEYS.LOAN_LENDER_EMAIL, FIELD_KEYS.LOAN_LENDER_ADDRESS, FIELD_KEYS.LOAN_LENDER_ASK_FOR, FIELD_KEYS.LOAN_LENDER_ASK_FOR_PHONE, FIELD_KEYS.LOAN_LENDER_ASK_FOR_EMAIL, FIELD_KEYS.LOAN_LENDER_ASK_FOR_ADDRESS], // s235 PR B (D-235.18(3)): the company card
    "yes.owing": [FIELD_KEYS.LOAN_BALANCE],
    "yes.monthly": [FIELD_KEYS.LOAN_PAYMENT],
    "yes.cosign": [FIELD_KEYS.LOAN_CO_BORROWER, FIELD_KEYS.LOAN_CO_BORROWER_PHONE, FIELD_KEYS.LOAN_CO_BORROWER_EMAIL, FIELD_KEYS.LOAN_CO_BORROWER_ADDRESS],
    // s235 PR B (D-235.20(4), rule 150): the insurer and the broker are company cards on the asset's ONE policy.
    "yes.ins": [FIELD_KEYS.HAS_INSURANCE],
    "yes.insurer": [FIELD_KEYS.POLICY_INSURER, FIELD_KEYS.POLICY_INSURER_PHONE, FIELD_KEYS.POLICY_INSURER_EMAIL, FIELD_KEYS.POLICY_INSURER_ADDRESS, FIELD_KEYS.POLICY_INSURER_ASK_FOR, FIELD_KEYS.POLICY_INSURER_ASK_FOR_PHONE, FIELD_KEYS.POLICY_INSURER_ASK_FOR_EMAIL, FIELD_KEYS.POLICY_INSURER_ASK_FOR_ADDRESS],
    "yes.brk": [FIELD_KEYS.POLICY_DEALT_WITH],
    "yes.broker": [FIELD_KEYS.POLICY_BROKER, FIELD_KEYS.POLICY_BROKER_PHONE, FIELD_KEYS.POLICY_BROKER_EMAIL, FIELD_KEYS.POLICY_BROKER_ADDRESS, FIELD_KEYS.POLICY_BROKER_ASK_FOR, FIELD_KEYS.POLICY_BROKER_ASK_FOR_PHONE, FIELD_KEYS.POLICY_BROKER_ASK_FOR_EMAIL, FIELD_KEYS.POLICY_BROKER_ASK_FOR_ADDRESS],
    "yes.will": [FIELD_KEYS.LEFT_TO, FIELD_KEYS.LEFT_TO_PERSON, FIELD_KEYS.LEFT_TO_PERSON_PHONE, FIELD_KEYS.LEFT_TO_PERSON_EMAIL, FIELD_KEYS.LEFT_TO_PERSON_ADDRESS], // s234 (PR 3): Yes / No / Not sure; s235: + the card
  },
  val: {
    "yes.item": [FIELD_KEYS.DESCRIPTION],
    "yes.where": [FIELD_KEYS.LOCATION],
    "yes.worth": [FIELD_KEYS.VALUE],
    "yes.will": [FIELD_KEYS.LEFT_TO, FIELD_KEYS.LEFT_TO_PERSON, FIELD_KEYS.LEFT_TO_PERSON_PHONE, FIELD_KEYS.LEFT_TO_PERSON_EMAIL, FIELD_KEYS.LEFT_TO_PERSON_ADDRESS], // s234 (PR 3): Yes / No / Not sure; s235: + the card
  },
  // s232 PR C (ruling 3): a firearm is a Valuables row, category Firearms.
  fa: {
    "yes.kind": [FIELD_KEYS.FIREARM_KIND],
    "yes.where": [FIELD_KEYS.LOCATION],
    "yes.taker": [FIELD_KEYS.LEGAL_TAKER, FIELD_KEYS.LEGAL_TAKER_PHONE, FIELD_KEYS.LEGAL_TAKER_EMAIL, FIELD_KEYS.LEGAL_TAKER_ADDRESS],
    "yes.safe": [FIELD_KEYS.IN_LOCKED_STORAGE],
    "yes.will": [FIELD_KEYS.LEFT_TO, FIELD_KEYS.LEFT_TO_PERSON, FIELD_KEYS.LEFT_TO_PERSON_PHONE, FIELD_KEYS.LEFT_TO_PERSON_EMAIL, FIELD_KEYS.LEFT_TO_PERSON_ADDRESS], // s234 (PR 3): Yes / No / Not sure; s235: + the card
  },
  // s232 PR C (ruling 4): a Safes and Locked Places row (located_items). The combination is never asked (rules 1–4).
  safes: {
    "yes.kind": [FIELD_KEYS.PLACE_KIND],
    "yes.where": [FIELD_KEYS.LOCATION],
    "yes.contents": [FIELD_KEYS.CONTENTS],
    "yes.lock": [FIELD_KEYS.LOCK_KIND],
    "yes.keyat": [FIELD_KEYS.KEY_LOCATION],
    "yes.opener": [FIELD_KEYS.OPENER, FIELD_KEYS.OPENER_PHONE, FIELD_KEYS.OPENER_EMAIL, FIELD_KEYS.OPENER_ADDRESS],
  },
};

/**
 * s233 (D-233.81) — the pinned home row's own lines (a table's `pinLines`, board v440): cells only the pinned row asks.
 * The parity census reads a table's cells, not these, so they are mapped here; the row reads this after CELL_FIELD.
 * The paid deposit and the keys are the rent row's own cells (CELL_FIELD `rent.lmrpaid`, `rent.keys`).
 */
export const PIN_CELL_FIELD: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  prop: { "rent.cotenant": [FIELD_KEYS.CO_TENANT, FIELD_KEYS.CO_TENANT_PHONE, FIELD_KEYS.CO_TENANT_EMAIL, FIELD_KEYS.CO_TENANT_ADDRESS] },
};

/** s232 — a drawn, unbuilt screen's cells: each accounted for, none mapped. */
function unbuilt(...keys: string[]): Readonly<Record<string, null>> {
  return Object.fromEntries(keys.map((k) => [k, null]));
}

/**
 * s231 (D-231.59, then Reade "agree a"): cells DRAWN, NOT BUILT, on a BUILT screen. The `null` convention above is
 * per screen; this names the one cell of a built screen that the board draws ahead of the product. A listed cell must
 * still be in CELL_FIELD as `null` and on the board (else the check fails); it is exempt ONLY from "a built screen's
 * cell maps to a field" and "a built screen's options have stored values". An unlisted `null` on a built screen
 * still fails. Remove the entry when the build gives the cell its field and stored values.
 */
export const DRAWN_CELLS: Readonly<Record<string, readonly string[]>> = {
  // s232 (D-232.22): `exec.bk` was built (BACKUP-NUDGE-1 PR 1) and moved to CELL_CLIENT_COLUMN.
  // s232 ruling 5: chapter two's insurer lines wait for the Insurance part (Window 3); PR C builds them after it merges.
  // s234 (CH2-STORAGE-PR3): "Rented out?", its tenant card and "Person named in will" are built (D-233.70(3)–(5)).
  // s235 PR B (D-235.20(4)): the insurer lines are built — nothing on chapter two is drawn ahead of the product now.
};

/**
 * s234 walk (item B, D-234.37): a yes/no whose `then` branch ALSO opens on another stored answer of the row, unanswered or
 * not — "Rented out? Yes" opens Who is the tenant? and the rent deposit, and so does an Investment Property (the classic
 * fields' "Rented out = Yes, or Investment Property"). The question itself is left as it is.
 */
export const BRANCH_ALSO_OPENS: Readonly<Record<string, Readonly<Record<string, { branch: string; field: string; equals: string }>>>> = {
  prop: { "own.rentout": { branch: "Yes", field: FIELD_KEYS.PROP_TYPE, equals: "Investment Property" } },
};

/**
 * s232 (D-232.8, D-232.22) — a cell on a built screen whose answer belongs to the PLAN, not to one row: it lands on a
 * `clients` column (the drizzle property name), never a field. It is in CELL_FIELD as `null` (it maps to no field) and
 * the parity check counts it as mapped; its options still need stored values in OPTION_VALUE. The column must exist
 * on `clients` (board-parity.test.ts).
 */
export const CELL_CLIENT_COLUMN: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  exec: { bk: "willNamesBackup" },
};

/**
 * Board option wording → stored value, on the built screens. A picker whose stored values ARE the board's
 * words is still listed (identity), so a new option on the board fails the check instead of storing a
 * wording nobody agreed to store.
 */
/** s234 (D-233.70(5), rule 149): "Person named in will" — the classic field's own words (Yes → will, No → no_one, Not sure). */
const WILL = { Yes: "Yes", No: "No", "Not sure": "Not sure" } as const;

export const OPTION_VALUE: Readonly<Record<string, Readonly<Record<string, Readonly<Record<string, string>>>>>> = {
  // s237 chapter three PR 2 — Work's "What kind": the board's word → the Business row's stored label. Where the classic page
  // already has the choice, its own label is stored (so both pages show one answer); LLP and Professional corporation are
  // new and stored as the board words — word for word, their kind "other" (an unlisted label, D-233.44(4)).
  work: {
    kind: {
      "Corporation": "Corporation",
      "Partnership": "Partnership",
      "Limited liability partnership (LLP)": "Limited liability partnership (LLP)",
      "Professional corporation": "Professional corporation",
      "Sole proprietorship": "Sole Proprietorship",
      "Self-employed or freelance": "Self-Employed / Freelance",
      "Farm": "Farm",
      "Holding company": "Holding Company",
      "Other — type it": "Other",
    },
  },
  // ── Chapter two (s232, PR B) ──
  prop: {
    "own.kind": {
      "A second home": "Secondary Residence",
      "A cottage or cabin": "Cottage or Cabin",
      Land: "Vacant Land",
      "A timeshare": "Timeshare",
      "A parking space": "Parking Space",
      "Other — type it": "Other",
    },
    "own.lmrtaken": { Yes: "true", No: "false" },
    "own.rentout": { Yes: "true", No: "false" },
    "own.will": WILL,
    "own.tenure": {
      "Jointly — it goes to them": "Joint tenancy (with right of survivorship)",
      "In shares — mine follows my will": "Tenants in common",
      // D-226.6: "Not sure" is co_owner — stored in its own words, appended to the choices (never "Other / unsure").
      "Not sure": "Co-owned, not sure how",
    },
    "own.gift": { Yes: "Yes", "No — I added them to help": "No — I added them to help", "Not sure": "Not sure" },
    // A yes_no field stores "true" / "false" (the card-path convention); the board shows Yes / No.
    "own.mortgage": { Yes: "true", No: "false" },
    "own.ins": { Yes: "true", No: "false" }, // s235 PR B
    "own.brk": { "A broker or agent": "A broker or agent", "Direct with the insurer": "Direct with the insurer", "Not sure": "Not sure" }, // s235 PR B
    "own.protect": { Yes: "true", No: "false" },
    "rent.kind": {
      "A storage unit": "Storage Unit",
      "A parking space": "Parking",
      "A flat or house": "Flat or House",
      "A garage or workshop": "Garage or Workshop",
      "A locker": "Locker",
      "Other — type it": "Other",
    },
    "rent.lease": { "A fixed term": "A fixed term", "Month to month": "Month to month" },
    "rent.lmrpaid": { Yes: "true", No: "false" },
    // Ruling 6: a care home is the care_residence kind; any other family or shared home is "Other".
    "fam.care": { Yes: "Care Residence", No: "Other" },
    "fam.deposit": { Yes: "Yes", No: "No", "Not sure": "Not sure" },
  },
  veh: {
    "yes.kind": {
      Car: "Car", Motorcycle: "Motorcycle", Boat: "Boat", Trailer: "Trailer", "RV or camper": "RV / Motorhome",
      "Snowmobile or ATV": "ATV / Snowmobile", Aircraft: "Aircraft", "Other — type it": "Other",
    },
    "yes.loan": { Yes: "true", No: "false" },
    "yes.ins": { Yes: "true", No: "false" }, // s235 PR B
    "yes.brk": { "A broker or agent": "A broker or agent", "Direct with the insurer": "Direct with the insurer", "Not sure": "Not sure" }, // s235 PR B
    "yes.will": WILL,
  },
  val: { "yes.will": WILL },
  fa: {
    "yes.kind": { Rifle: "Rifle", Shotgun: "Shotgun", Handgun: "Handgun", Restricted: "Restricted", Prohibited: "Prohibited", "Other — type it": "Other" },
    "yes.safe": { Yes: "true", No: "false" },
    "yes.will": WILL,
  },
  safes: {
    "yes.kind": { "A safe": "A safe", "A safety deposit box": "A safety deposit box", "A locked cabinet": "A locked cabinet", "A hiding place": "A hiding place", "Other — type it": "Other" },
    "yes.lock": { "A key": "A key", "A dial": "A dial", "A keypad": "A keypad", "Other — type it": "Other" },
  },

  kids: {
    // The stored shared choices (dependent-relationships.ts) — the gov-form child list and every reader keep them.
    crel: { "My son": "Son", "My daughter": "Daughter", "My stepchild": "Stepchild" },
    drel: {
      // "My adult child" is a DEPENDANT (never Son/Daughter, which kindOf files as a child) — CONTACT-CARD-ONE-1.
      "My adult child": "Adult child",
      "My grandchild": "Grandchild",
      "I’m their guardian": "Ward", // s234 (D-234.104): display words only, was "Someone I am guardian for"
      "My parent": "Parent",
      "My brother or sister": "Sibling",
      "Other — type it": "Other dependent",
    },
    inwill: { Yes: "Yes", No: "No", "Not sure": "Not sure" },
  },
  daily: {
    grade: {
      "Not in school yet": "Not in school yet",
      "JK or SK": "JK or SK",
      "Grades 1 to 8": "Grades 1 to 8",
      "Grades 9 to 12": "Grades 9 to 12",
      "College or university": "College or university",
    },
    // A yes_no field stores "true" / "false" (the card-path convention); the board shows Yes / No.
    faith: { Yes: "true", No: "false" },
  },
  exec: {
    // `relationship` on legal-your-executor is free TEXT with no shared vocabulary, so the board's words are what
    // is stored (identity) — except the "type it" choice, which stores the plain "Other" (the kids precedent,
    // where it stores "Other dependent"); the type-your-own box is not built.
    rel: {
      "My spouse or partner": "My spouse or partner",
      "My daughter": "My daughter",
      "My son": "My son",
      "My brother or sister": "My brother or sister",
      "A friend": "A friend",
      "My lawyer or accountant": "My lawyer or accountant",
      "Other — type it": "Other",
    },
    // s232 (D-232.8): the stored values of the `will_names_backup` enum (WILL_NAMES_BACKUP).
    bk: { Yes: "yes", No: "no", "Not sure": "not_sure" },
  },
  pets: {
    // s224 (D-223.36 ruling 3). The board's seven words → the stored `pet_type` choices. Six are the
    // stored words themselves (identity), so a NEW option on the board fails the check rather than
    // quietly storing a wording nobody agreed to store. Two are not identities and both are ruled:
    //  • "Cattle or other livestock" → the surviving stored "Cattle", which the board's own phrase
    //    widens in words only — the four narrower livestock choices were retired by the same ruling;
    //  • "Small animal or reptile" → a choice APPENDED to the spec in the list's own noun style,
    //    the s223 beneficiaries precedent, because the board offers it and nothing stored matched.
    // "Other — type it" stores the plain "Other" — the `kids` / `exec` precedent.
    kind: {
      Dog: "Dog",
      Cat: "Cat",
      Bird: "Bird",
      Horse: "Horse",
      "Cattle or other livestock": "Cattle",
      "Small animal or reptile": "Small animal or reptile",
      "Other — type it": "Other",
    },
  },
  benef: {
    // The stored shared choices (legal.ts beneficiary_relationship). s223 (D-222.10 part 4) APPENDED the four the
    // board offers that had no stored equivalent, in the list's own noun style. Reade has NOT ruled a separate
    // charity choice — the stored "Charity / Organization" stands.
    rel: {
      "My parent": "Parent",
      "My brother or sister": "Sibling",
      "My grandchild": "Grandchild",
      "My niece or nephew": "Niece or nephew",
      "A friend": "Friend",
      "A godchild": "Godchild",
      "A neighbour": "Neighbour",
      "A colleague": "Colleague",
      "A charity or organisation": "Charity / Organization",
      "Other — type it": "Other",
    },
    // A yes_no field stores "true" / "false" (the card-path convention); the board shows No / Yes.
    minor: { No: "false", Yes: "true" },
  },
};

/**
 * s223 — `fromKid` (D-222.10 part 4): what a cell carries in when the row's person is one of the planner's OWN
 * children from chapter one. The board says only the word it shows (`fromKid: 'My child'`, `'Yes'`,
 * `'[from chapter one]'`); this says where the value comes from:
 *   • `fixed`   — a stored value (the relationship "Child");
 *   • `under18` — the child's age today, from their date of birth: "true" / "false". Unknown when there is no
 *                 date of birth, and then NOT carried — the cell is asked like any other;
 *   • `dob`     — the child's date of birth. Not carried when there is none.
 * Keyed by screen and board cell key; every `fromKid` cell on a built screen must be declared here (test-pinned).
 */
export type FromKidCarry = { kind: "fixed"; value: string } | { kind: "under18" } | { kind: "dob" };
export const FROM_KID: Readonly<Record<string, Readonly<Record<string, FromKidCarry>>>> = {
  benef: {
    rel: { kind: "fixed", value: "Child" },
    minor: { kind: "under18" },
    dob: { kind: "dob" },
  },
};
